import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import type { Dispatch, SetStateAction } from "react";
import { financeApi } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import type { ImportApplyResult, ImportBatch } from "../../lib/types";
import { importPollingIntervalMs, isBatchPolling } from "./importHelpers";

type ImportJobPollingOptions = {
  groupId: string;
  batchId: string;
  batch?: ImportBatch;
  setAppliedResult: Dispatch<SetStateAction<ImportApplyResult | null>>;
};

export function useImportJobPolling({ groupId, batchId, batch, setAppliedResult }: ImportJobPollingOptions) {
  const queryClient = useQueryClient();
  const terminalStatusRefreshRef = useRef("");
  const statusQuery = useQuery({
    queryKey: queryKeys.importBatchStatus(groupId, batchId),
    queryFn: () => financeApi.importBatchStatus(groupId, batchId),
    enabled: Boolean(batchId && isBatchPolling(batch?.status)),
    refetchInterval: (query) => {
      const current = query.state.data;
      return !current || isBatchPolling(current.status) ? importPollingIntervalMs : false;
    },
  });

  useEffect(() => {
    const nextStatus = statusQuery.data;
    if (!nextStatus || !batchId) return;
    queryClient.setQueryData<ImportBatch>(queryKeys.importBatch(groupId, batchId), (current) =>
      current ? { ...current, ...nextStatus } : current,
    );
    const terminalKey = `${batchId}:${nextStatus.status}`;
    if (!isBatchPolling(nextStatus.status) && terminalStatusRefreshRef.current !== terminalKey) {
      terminalStatusRefreshRef.current = terminalKey;
      void financeApi
        .importBatch(groupId, batchId)
        .then((completedBatch) => {
          queryClient.setQueryData(queryKeys.importBatch(groupId, batchId), completedBatch);
          if (completedBatch.status !== "applied") return;
          setAppliedResult(completedBatch);
          void Promise.all([
            queryClient.invalidateQueries({ queryKey: queryKeys.accounts(groupId) }),
            queryClient.invalidateQueries({ queryKey: queryKeys.categories(groupId) }),
            queryClient.invalidateQueries({ queryKey: queryKeys.records }),
            queryClient.invalidateQueries({ queryKey: queryKeys.deletedRecords }),
            queryClient.invalidateQueries({ queryKey: queryKeys.summary }),
          ]);
        })
        .catch(() => queryClient.invalidateQueries({ queryKey: queryKeys.importBatch(groupId, batchId) }));
      void queryClient.invalidateQueries({ queryKey: queryKeys.importBatches(groupId) });
    }
  }, [batchId, groupId, queryClient, setAppliedResult, statusQuery.data]);
}
